if (!customElements.get('product-fixed-bar')) {
  customElements.define('product-fixed-bar', class ProductFixedBar extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.variantId = parseInt(this.dataset.variantId);
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-add-to-cart]');
      this.priceElement = this.querySelector('.product-fixed-bar__price');
      this.titleElement = this.querySelector('.product-fixed-bar__title');
      this.imageElement = this.querySelector('[data-product-image]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.toggleButton = this.querySelector('[data-toggle-bar]');
      
      // Get product JSON data from this component's JSON
      const productJsonElement = this.querySelector('.pfx-bar-product-json') || document.querySelector('.pfx-bar-product-json');
      if (productJsonElement) {
        try {
          this.productData = JSON.parse(productJsonElement.textContent);
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }

      // Get the variant select element (self-contained within this component)
      this.mainVariantSelect = this.querySelector('[data-pfx-bar-option-main]');
      this.optionSelects = this.querySelectorAll('[data-pfx-bar-option-select]');

      // State for variant selection
      this.state = {
        id: null,
        options: []
      };

      this.variants = {};

      // State for bar visibility
      this.hasBeenShown = false;
      this.isManuallyCollapsed = false;
      this.scrollThreshold = 300; // Show bar after scrolling 300px

      this.init();
    }

    init() {
      // Initialize variant mapping
      if (this.mainVariantSelect) {
        this.variants = Array.from(this.mainVariantSelect.options).reduce((variants, option) => {
          variants[option.textContent] = option.value;
          return variants;
        }, {});
      }

      // Initialize scroll detection
      this.initScrollDetection();

      // Initialize toggle button
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent triggering bar click
          this.handleToggleClick();
        });
      }

      // Add click handler to the bar itself - only when collapsed
      this.addEventListener('click', this.handleBarClick.bind(this));

      // Initialize variant selection system
      this.initVariantSelection();

      // Handle add to cart
      if (this.button) {
        this.button.addEventListener('click', this.handleAddToCart.bind(this));
      }

      // Initialize variant state
      this.getInitialState();
      this.updateVariant(this.state.id);
    }

    initScrollDetection() {
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const scrollY = window.scrollY || window.pageYOffset;
            
            // Show bar when user scrolls past threshold
            if (!this.hasBeenShown && scrollY > this.scrollThreshold) {
              this.showBar();
              this.hasBeenShown = true;
            }
            
            ticking = false;
          });
          
          ticking = true;
        }
      };

      // Use passive listener for better performance
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Check initial scroll position
      if (window.scrollY > this.scrollThreshold) {
        this.showBar();
        this.hasBeenShown = true;
      }
    }

    showBar() {
      // Always show the bar (either expanded or collapsed based on manual state)
      this.classList.add('is-visible');
      
      // If manually collapsed, ensure collapsed state is maintained
      if (this.isManuallyCollapsed) {
        this.classList.add('is-collapsed');
      } else {
        this.classList.remove('is-collapsed');
      }
    }

    handleToggleClick() {
      // When expanded: clicking icon should collapse
      // When collapsed: clicking icon should expand (handled by bar click, but we stop propagation)
      if (this.classList.contains('is-collapsed')) {
        // Already collapsed, expand it
        this.isManuallyCollapsed = false;
        this.classList.remove('is-collapsed');
      } else {
        // Expanded, collapse it
        this.isManuallyCollapsed = true;
        this.classList.add('is-collapsed');
      }
      
      // Ensure bar is visible
      if (!this.classList.contains('is-visible')) {
        this.classList.add('is-visible');
      }
    }

    handleBarClick(e) {
      // Only handle clicks when collapsed - clicking anywhere on the collapsed bar should expand it
      if (this.classList.contains('is-collapsed')) {
        // Don't expand if the click originated from the toggle button (it handles its own click)
        if (this.toggleButton && this.toggleButton.contains(e.target)) {
          return; // Toggle button handles its own click
        }
        
        // Expand the bar when clicking anywhere on the collapsed white box
        this.isManuallyCollapsed = false;
        this.classList.remove('is-collapsed');
      }
    }

    initVariantSelection() {
      if (!this.optionSelects || this.optionSelects.length === 0) return;

      // Initialize select dropdown handlers
      this.optionSelects.forEach((select) => {
        const optionIndex = parseInt(select.getAttribute('data-pfx-bar-index'), 10);

        // Set initial value
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          this.state.options[optionIndex] = selectedOption.getAttribute('data-pfx-bar-value-handle') || selectedOption.value;
        }

        // Handle select changes
        select.addEventListener('change', (e) => {
          const selectedOption = e.target.options[e.target.selectedIndex];
          if (selectedOption) {
            this.state.options[optionIndex] = selectedOption.getAttribute('data-pfx-bar-value-handle') || selectedOption.value;
            this.updateSelection();
          }
        });
      });
    }

    getInitialState() {
      if (!this.optionSelects || this.optionSelects.length === 0) return;

      // Build initial options array from selected options in dropdowns
      this.optionSelects.forEach((select) => {
        const optionIndex = parseInt(select.getAttribute('data-pfx-bar-index'), 10);
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          this.state.options[optionIndex] = selectedOption.getAttribute('data-pfx-bar-value-handle') || selectedOption.value;
        }
      });

      // Find initial variant ID using product JSON data (more reliable)
      if (this.productData && this.productData.product) {
        const matchingVariant = this.productData.product.variants.find(variant => {
          return variant.options.every((optionValue, index) => {
            const selectedHandle = this.state.options[index];
            if (!selectedHandle || !optionValue) return false;
            const optionHandle = optionValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            return optionHandle === selectedHandle;
          });
        });
        
        if (matchingVariant) {
          this.state.id = matchingVariant.id.toString();
          return;
        }
      }

      // Fallback: Match variant ID with handleized option string from variants map
      const variantKey = this.state.options.filter(Boolean).join('-');
      this.state.id = this.variants[variantKey] || this.variantId;
    }

    updateSelection() {
      // First try to find variant using product JSON data (more reliable)
      if (this.productData && this.productData.product) {
        const matchingVariant = this.productData.product.variants.find(variant => {
          return variant.options.every((optionValue, index) => {
            const selectedHandle = this.state.options[index];
            if (!selectedHandle || !optionValue) return false;
            // Compare handleized values
            const optionHandle = optionValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            return optionHandle === selectedHandle;
          });
        });
        
        if (matchingVariant) {
          this.state.id = matchingVariant.id.toString();
          this.updateVariant(this.state.id);
          return;
        }
      }

      // Fallback: Match variant ID with handleized option string from variants map
      const variantKey = this.state.options.filter(Boolean).join('-');
      this.state.id = this.variants[variantKey];

      if (this.state.id) {
        this.updateVariant(this.state.id);
      }
    }

    updateVariant(variantId) {
      if (!variantId) return;

      this.variantId = parseInt(variantId);

      // Update the hidden select element
      if (this.mainVariantSelect) {
        this.mainVariantSelect.value = variantId;
      }

      // Update price
      this.updatePrice();

      // Update image
      this.updateImage();

      // Update availability
      this.updateAvailability();

      // Update select dropdown states
      this.updateSelectStates();
    }

    updatePrice() {
      if (!this.mainVariantSelect || !this.priceElement) return;

      const selectedOption = this.mainVariantSelect.options[this.mainVariantSelect.selectedIndex];
      if (!selectedOption) return;

      const price = selectedOption.getAttribute('data-variant-price');
      const comparePrice = selectedOption.getAttribute('data-variant-compare');
      const priceElement = this.priceElement.querySelector('.price');

      if (priceElement && price) {
        const isOnSale = comparePrice && parseFloat(comparePrice.replace(/[^0-9.]/g, '')) > parseFloat(price.replace(/[^0-9.]/g, ''));
        
        priceElement.className = 'price price--large';
        if (isOnSale) {
          priceElement.classList.add('price--on-sale');
        }

        if (isOnSale) {
          priceElement.innerHTML = `
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <s class="price-item price-item--regular">
                <span data-selected-var-compare>${comparePrice}</span>
              </s>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">
                <span data-selected-var-price>${price}</span>
              </span>
            </div>
          `;
        } else {
          priceElement.innerHTML = `
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">
                <span data-selected-var-price>${price}</span>
              </span>
            </div>
          `;
        }
      }
    }

    updateImage() {
      if (!this.imageElement || !this.productData || !this.productData.product) return;

      const variant = this.productData.product.variants.find(v => v.id === this.variantId);
      if (!variant) return;

      // Get variant image or fall back to product featured image
      let imageUrl = null;
      
      if (variant.featured_image) {
        imageUrl = typeof variant.featured_image === 'string' 
          ? variant.featured_image 
          : (variant.featured_image.src || variant.featured_image);
      } else if (this.productData.product.featured_image) {
        imageUrl = typeof this.productData.product.featured_image === 'string'
          ? this.productData.product.featured_image
          : (this.productData.product.featured_image.src || this.productData.product.featured_image);
      }

      if (imageUrl) {
        // Convert to 100x100 size for thumbnail
        let imageSrc = imageUrl;
        if (imageUrl.includes('cdn.shopify.com')) {
          imageSrc = imageUrl.replace(/\_\d+x\d+\.(jpg|jpeg|png|gif|webp)/i, '_100x100.$1');
          if (imageSrc === imageUrl && !imageUrl.match(/_\d+x\d+\./)) {
            imageSrc = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_100x100.$1$2');
          }
        }
        
        const imageSrc2x = imageSrc.replace('_100x100', '_200x200');
        
        this.imageElement.src = imageSrc;
        this.imageElement.srcset = `${imageSrc} 1x, ${imageSrc2x} 2x`;
        this.imageElement.alt = variant.title || this.productData.product.title;
      }
    }

    updateAvailability() {
      if (!this.button || !this.mainVariantSelect) return;

      const selectedOption = this.mainVariantSelect.options[this.mainVariantSelect.selectedIndex];
      if (!selectedOption) return;

      let inventoryQty = parseInt(selectedOption.getAttribute('data-variant-inventory-quantity') || '0', 10);
      const inventoryPolicy = selectedOption.getAttribute('data-variant-inventory-policy');
      const inventoryManagement = selectedOption.getAttribute('data-variant-inventory-management');
      
      let mainPolicy = inventoryPolicy === 'deny' || false;
      const warehouse = window.restrictedWarehouse;

      if (warehouse && selectedOption.dataset.locationBasedQuantity) {
        try {
          const locationQuantities = JSON.parse(selectedOption.dataset.locationBasedQuantity);
          if (locationQuantities && typeof locationQuantities[warehouse] !== 'undefined') {
            inventoryQty = locationQuantities[warehouse];
            mainPolicy = true; // Enforce for location-based
            console.log(`[Fixed Bar Location Inv] ${warehouse}: ${inventoryQty}`);
          }
        } catch (e) {
          console.error('Error parsing location-based quantity:', e);
        }
      }

      const isAvailable = selectedOption.disabled === false && !(inventoryQty <= 0 && mainPolicy && inventoryManagement);

      const buttonText = this.button.querySelector('.product-fixed-bar__button-text');
      if (buttonText) {
        if (isAvailable) {
          this.button.disabled = false;
          buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.button.disabled = true;
          buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }
    }

    updateSelectStates() {
      if (!this.productData || !this.productData.product) return;

      const variant = this.productData.product.variants.find(v => v.id === this.variantId);
      if (!variant) return;

      // Update select dropdown states based on selected variant
      this.optionSelects.forEach((select) => {
        const optionIndex = parseInt(select.getAttribute('data-pfx-bar-index'), 10);
        const variantOptionValue = variant.options[optionIndex];
        const variantOptionHandle = variantOptionValue ? variantOptionValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : null;

        // Find and select the matching option
        for (let i = 0; i < select.options.length; i++) {
          const option = select.options[i];
          const optionHandle = option.getAttribute('data-pfx-bar-value-handle') || option.value;
          if (optionHandle === variantOptionHandle) {
            select.selectedIndex = i;
            break;
          }
        }
      });
    }

    async handleAddToCart(event) {
      event.preventDefault();

      if (this.button.classList.contains('is-loading') || this.button.disabled) {
        return;
      }

      // Show loading state
      this.button.classList.add('is-loading');
      this.button.disabled = true;

      try {
        if (!this.variantId) {
          throw new Error('No variant selected');
        }

        // Use the simpler /cart/add.js API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            id: this.variantId, 
            quantity: 1 
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check if there's an error in the response
        if (data.status) {
          console.error('Add to cart error:', data.description);
          this.showError(data.description);
          this.button.classList.remove('is-loading');
          this.button.disabled = false;
          return;
        }

        // Success - fetch cart and open drawer
        const cart = await this.fetchCart();
        
        if (this.cartDrawer) {
          this.cartDrawer.open(cart);
          this.cartDrawer.setActiveElement(this.button);
          
          // Dispatch cart update event
          const cartUpdateEvent = new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart }
          });
          document.dispatchEvent(cartUpdateEvent);
        }else {
          // redirect to cart page
          window.location.href = '/cart';
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError('Unable to add product to cart. Please try again.');
      } finally {
        // Remove loading state
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'include',
      });
      return await response.json();
    }

    showError(message) {
      console.error(message);
      // Optionally show a toast or error message to the user
    }
  });
}
